import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Button,
  Box,
  CircularProgress,
  Alert,
  alpha,
} from '@mui/material';
import { Delete as DeleteIcon, Edit as EditIcon } from '@mui/icons-material';

// ─── Design tokens ────────────────────────────────────────────────────────────
const NEON  = '#38BDF8';
const NEON2 = '#818CF8';

const SURFACE_BASE = '#050d1a';
const SURFACE_MID  = '#0a1628';
const SURFACE_UP   = '#0f1f38';

const B900 = '#1E3A8A';
const B700 = '#1D4ED8';

const DOT_GRID = 'none';

const DEL_CSS = ``;

// ─────────────────────────────────────────────────────────────────────────────
const ConfirmDeleteDialog = ({
  open = false,
  onClose,
  onConfirm,
  title = 'Confirm Delete',
  itemType = 'item',
  itemName = '',
  description = 'This action cannot be undone.',
  confirmLabel = 'Delete',
  cancelLabel = 'Cancel',
  loading = false,
  // `variant` controls the default icon + accent colors.
  // Kept optional so existing delete dialogs continue to render the delete icon + red.
  variant = 'delete', // 'delete' | 'edit'
  // Allows using the same dialog for both delete and update flows.
  // If omitted, it auto-derives from `variant`.
  actionVerb = null, // 'delete' | 'update'
  // Override the icon completely if you want full control.
  icon = null,
  // Override the accent color used for icon, title, and confirm button.
  accentColor = null,
  errorMessage = null,
  confirmDisabled = false,
}) => {
  // Inject keyframes once
  React.useEffect(() => {
    const cssId = 'confirm-del-css';
    if (!document.getElementById(cssId)) {
      const el = document.createElement('style');
      el.id = cssId; el.innerHTML = DEL_CSS;
      document.head.appendChild(el);
    }
  }, []);

  const resolvedVariant = variant;
  const resolvedAccent = accentColor
    ?? (resolvedVariant === 'edit' ? NEON : '#f87171');

  const resolvedActionVerb =
    actionVerb ?? (resolvedVariant === 'edit' ? 'update' : 'delete');

  const defaultIcon = resolvedVariant === 'edit' ? <EditIcon /> : <DeleteIcon />;
  const DialogIcon = icon ?? defaultIcon;

  const isDeleteFlow = resolvedVariant === 'delete';

  return (
    <Dialog
      open={open}
      onClose={loading ? undefined : onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        elevation: 0,
        sx: {
          borderRadius: '16px',
          background: `linear-gradient(160deg, ${alpha(SURFACE_BASE, 0.98)} 0%, ${alpha(SURFACE_MID, 0.96)} 100%)`,
          border: `1px solid ${alpha(NEON, 0.17)}`,
          boxShadow: [
            `0 0 0 1px ${alpha(B900, 0.9)}`,
            `0 24px 64px ${alpha('#000', 0.6)}`,
            `0 4px 16px ${alpha(NEON, 0.06)}`,
            `inset 0 1px 0 ${alpha(NEON, 0.1)}`,
          ].join(', '),
          backdropFilter: 'blur(24px)',
          overflow: 'hidden',
          position: 'relative',
        },
      }}
      BackdropProps={{
        sx: { background: 'rgba(3,9,18,0.72)', backdropFilter: 'blur(4px)' },
      }}
    >
      {/* Textured sweep/glow removed for a clean SaaS dialog */}
      <Box sx={{ display: 'none' }} />

      {/* ── Title ── */}
      <DialogTitle sx={{
        pt: 2.5, pb: 1.5, position: 'relative', zIndex: 1,
        background: `linear-gradient(135deg, ${alpha(resolvedAccent, 0.04)}, transparent)`,
        borderBottom: `1px solid ${alpha(NEON, 0.1)}`,
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Box sx={{
            width: 36, height: 36, borderRadius: '10px', flexShrink: 0,
            background: `linear-gradient(135deg, ${alpha(resolvedAccent, 0.22)}, ${alpha(resolvedAccent, 0.5)})`,
            border: `1px solid ${alpha(resolvedAccent, 0.32)}`,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            color: resolvedAccent,
            boxShadow: `0 0 14px ${alpha(resolvedAccent, 0.25)}`,
          }}>
            {React.isValidElement(DialogIcon) ? React.cloneElement(DialogIcon, { sx: { fontSize: 18 } }) : DialogIcon}
          </Box>
          <Typography sx={{
            fontFamily: "'Rajdhani', sans-serif",
            fontWeight: 700, fontSize: '1.1rem',
            color: resolvedAccent, letterSpacing: 0.4,
          }}>
            {title}
          </Typography>
        </Box>
      </DialogTitle>

      {/* ── Content ── */}
      <DialogContent sx={{ pt: 2.5, pb: 2, position: 'relative', zIndex: 1 }}>
        <Typography sx={{
          fontFamily: "'DM Sans', sans-serif",
          fontSize: '0.9rem',
          color: alpha('#E2E8F0', 0.8),
          lineHeight: 1.65,
        }}>
          Are you sure you want to {resolvedActionVerb} the {itemType}{' '}
          {itemName ? (
            <Box component="span" sx={{ fontWeight: 700, color: alpha('#E2E8F0', 0.95) }}>
              "{itemName}"
            </Box>
          ) : null}?
        </Typography>
        <Typography sx={{
          fontFamily: "'DM Sans', sans-serif",
          fontSize: '0.82rem',
          color: alpha(resolvedAccent, 0.38),
          mt: 1, lineHeight: 1.55,
        }}>
          {description}
        </Typography>
        {errorMessage ? (
          <Alert
            severity="error"
            sx={{
              mt: 2,
              borderRadius: '10px',
              fontFamily: "'DM Sans', sans-serif",
              fontSize: '0.85rem',
              background: alpha('#450a0a', 0.92),
              border: '1px solid rgba(248,113,113,0.35)',
              color: '#fca5a5',
              '& .MuiAlert-icon': { color: '#f87171' },
            }}
          >
            {errorMessage}
          </Alert>
        ) : null}
      </DialogContent>

      {/* ── Actions ── */}
      <DialogActions sx={{
        px: 3, pb: 3, gap: 1.5, position: 'relative', zIndex: 1,
        background: `linear-gradient(135deg, ${alpha(SURFACE_UP, 0.35)}, transparent)`,
        borderTop: `1px solid ${alpha(NEON, 0.09)}`,
      }}>
        {/* Cancel */}
        <Button
          onClick={onClose}
          disabled={loading}
          sx={{
            borderRadius: '10px', px: 3,
            fontFamily: "'DM Sans', sans-serif", fontWeight: 600, fontSize: '0.875rem',
            color: alpha(NEON, 0.7),
            background: alpha(SURFACE_UP, 0.5),
            border: `1px solid ${alpha(NEON, 0.2)}`,
            transition: 'all 0.2s ease',
            '&:hover': {
              background: alpha(NEON, 0.07),
              borderColor: alpha(NEON, 0.45),
              color: NEON,
            },
            '&.Mui-disabled': { opacity: 0.35, color: alpha(NEON, 0.4) },
          }}
        >
          {cancelLabel}
        </Button>

        {/* Confirm — delete: solid red (MUI error + strong fill) so all screens match; edit: accent gradient */}
        <Button
          onClick={onConfirm}
          disabled={loading || confirmDisabled}
          variant="contained"
          color={isDeleteFlow ? 'error' : undefined}
          sx={{
            borderRadius: '10px',
            px: 4,
            fontFamily: "'DM Sans', sans-serif",
            fontWeight: 700,
            fontSize: '0.875rem',
            transition: 'all 0.22s ease',
            ...(isDeleteFlow
              ? {
                  background: 'linear-gradient(180deg, #EF4444 0%, #DC2626 100%)',
                  color: '#fff',
                  border: '1px solid rgba(254, 202, 202, 0.4)',
                  boxShadow: '0 4px 16px rgba(220, 38, 38, 0.45)',
                  '&:hover': {
                    background: 'linear-gradient(180deg, #F87171 0%, #B91C1C 100%)',
                    color: '#fff',
                    boxShadow: '0 6px 22px rgba(220, 38, 38, 0.55)',
                    transform: 'scale(1.02)',
                  },
                  '&:active': { transform: 'scale(0.99)' },
                  '&.Mui-disabled': {
                    background: alpha(SURFACE_UP, 0.75),
                    color: alpha('#fff', 0.35),
                    boxShadow: 'none',
                    border: `1px solid ${alpha('#f87171', 0.12)}`,
                  },
                }
              : {
                  background: `linear-gradient(135deg, ${alpha(resolvedAccent, 0.22)}, ${alpha(resolvedAccent, 0.5)})`,
                  border: `1px solid ${alpha(resolvedAccent, 0.32)}`,
                  color: resolvedAccent,
                  boxShadow: `0 0 16px ${alpha(resolvedAccent, 0.22)}`,
                  '&:hover': {
                    background: `linear-gradient(135deg, ${alpha(resolvedAccent, 0.32)}, ${alpha(resolvedAccent, 0.62)})`,
                    boxShadow: `0 0 24px ${alpha(resolvedAccent, 0.42)}`,
                    color: '#fff',
                    transform: 'scale(1.02)',
                  },
                  '&:active': { transform: 'scale(0.99)' },
                  '&.Mui-disabled': {
                    background: alpha(SURFACE_UP, 0.7),
                    color: alpha(resolvedAccent, 0.25),
                    boxShadow: 'none',
                    border: `1px solid ${alpha(resolvedAccent, 0.1)}`,
                  },
                }),
          }}
        >
          {loading
            ? <CircularProgress size={18} sx={{ color: alpha(resolvedAccent, 0.7) }} />
            : confirmLabel}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ConfirmDeleteDialog;